import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, FileText, Clock, Shield, Phone, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20GST%20Registration%20in%20Coimbatore";

const FAQS = [
  { q: "Who needs GST registration in India?", a: "GST registration is mandatory once your annual turnover crosses ₹40 lakh (₹20 lakh for service providers and special-category states). It is also mandatory — irrespective of turnover — for inter-state suppliers, e-commerce sellers, casual taxable persons, and businesses required to pay tax under reverse charge." },
  { q: "How long does GST registration take?", a: "With Aadhaar authentication, most GST registrations are issued within 3–7 working days from submission. If the officer raises a REG-03 query, expect 2–3 additional days for clarification and final approval." },
  { q: "Can I apply for GST voluntarily before crossing the turnover limit?", a: "Yes. Voluntary GST registration lets you claim input tax credit on your purchases and issue tax invoices to B2B customers — useful even if your turnover is below the threshold." },
  { q: "What is the cost of GST registration?", a: "The Government of India does not charge any fee for GST registration. WINTRUST SOLUTIONS charges a transparent professional fee for end-to-end filing, document verification, and follow-up with the GST officer." },
  { q: "What documents are required for GST registration?", a: "PAN, Aadhaar, address proof of business premises (rent agreement + NOC, or electricity bill), passport-size photo, bank account proof, and incorporation documents (for companies/LLPs/partnerships)." },
  { q: "Do I need separate GST registration for each state?", a: "Yes. If you have business operations or warehouses in multiple states, you need a separate GSTIN for each state. Stock transfers between your own warehouses across states are treated as inter-state supply." },
  { q: "What happens after I get my GSTIN?", a: "You must start filing GSTR-1 and GSTR-3B every month (or quarterly under QRMP), maintain proper invoices with GSTIN, and reconcile input tax credit. We offer ongoing GST return filing services to keep you fully compliant." },
];

const RELATED = [
  { to: "/gst-return-filing", label: "GST Return Filing", description: "Monthly GSTR-1 & GSTR-3B filing", tag: "Service" },
  { to: "/ecom-gst-registration", label: "E-Commerce GST Registration", description: "For Amazon, Flipkart, Meesho sellers", tag: "Service" },
  { to: "/gst-penalty-calculator", label: "GST Late Fee Calculator", description: "Check your late fee + 18% interest", tag: "Free Tool" },
  { to: "/gst-interest-calculator", label: "GST Interest Calculator", description: "Calculate interest on delayed payment", tag: "Free Tool" },
  { to: "/gst-notice-response-template", label: "GST Notice Response Template", description: "Free downloadable template", tag: "Template" },
  { to: "/compliance-roadmap", label: "Business Compliance Roadmap", description: "Find what your business needs in 60 sec", tag: "Tool" },
];

const GSTRegistrationPage = () => {
  useCanonical("/gst-registration");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "GST Registration in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Quick and hassle-free GST registration services for businesses in Coimbatore and Tamil Nadu."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>GST Registration in Coimbatore | Quick & Hassle-Free | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Get GST registration in Coimbatore with WINTRUST SOLUTIONS. Quick, hassle-free, and 100% compliant. Expert guidance, transparent pricing. Apply now!" />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Registration</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              GST Registration in Coimbatore | Quick, Hassle-Free & Compliant
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              GST (Goods and Services Tax) registration is a fundamental requirement for most businesses in India. At WINTRUST SOLUTIONS, we specialize in providing quick, hassle-free, and fully compliant GST registration services for businesses in Coimbatore and across Tamil Nadu.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">Get Expert GST Support</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why GST Registration */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Why is GST Registration Essential for Your Business?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { icon: <Shield size={24} />, title: "Legal Compliance", desc: "It is mandatory for businesses with an annual turnover exceeding the prescribed limit. Stay legally compliant and avoid penalties." },
              { icon: <FileText size={24} />, title: "Input Tax Credit (ITC)", desc: "Allows you to claim credit on the taxes you've paid on your business purchases, reducing your overall tax liability significantly." },
              { icon: <CheckCircle size={24} />, title: "Enhanced Business Credibility", desc: "A GSTIN (GST Identification Number) enhances the credibility and reputation of your business with customers and partners." },
              { icon: <Clock size={24} />, title: "Inter-State Business", desc: "You cannot conduct business between states without a GST registration. Expand your business across India legally." },
            ].map((item, index) => (
              <div key={index} className="flex gap-4 p-6 bg-slate-50 rounded-lg">
                <div className="text-gold flex-shrink-0">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Our GST Registration Process
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Initial Consultation", desc: "We start with a free consultation to understand your business structure and determine your GST requirements." },
              { step: "02", title: "Document Collection", desc: "We assist you in gathering all the necessary documents, such as PAN card, address proof, and bank details, through our secure digital portal." },
              { step: "03", title: "Application Filing", desc: "Our experts meticulously prepare and file your GST registration application (Form GST REG-01) with complete accuracy." },
              { step: "04", title: "Approval & ARN", desc: "We track the application and ensure you receive your Application Reference Number (ARN) and final GST registration certificate promptly." },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gold font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Documents Required for GST Registration
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "PAN Card of the Business/Proprietor",
              "Aadhaar Card of the Authorized Signatory",
              "Proof of Business Address (Electricity Bill, Rent Agreement)",
              "Bank Account Statement/Cancelled Cheque",
              "Passport Size Photograph",
              "Digital Signature Certificate (for Companies/LLPs)",
              "Certificate of Incorporation (for Companies)",
              "Partnership Deed (for Partnership Firms)",
            ].map((doc, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <CheckCircle className="text-gold flex-shrink-0" size={20} />
                <span className="text-navy">{doc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            Ready to Get Your GST Registration Done?
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Don't let paperwork and complex procedures slow you down. Contact WINTRUST SOLUTIONS today for a free consultation and let our experts handle your GST registration from start to finish.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            Get Expert GST Support
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="gst-reg-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Helpful resources for GST-registered businesses in Coimbatore"
        items={RELATED}
        testId="gst-reg-related"
      />

      <ContactForm />
    </>
  );
};

export default GSTRegistrationPage;
